import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import Layout from "../../components/Layout/Layout";
import HeaderWithFilter from "../../components/Layout/HeaderWithFilter";
import Loader from "../../components/Layout/Loader";
import ConfirmPopup from "../../components/Layout/ConfirmPopup";
import SearchBar from "../../components/Layout/SearchBar";
import { fetchWithToken } from "../../utils/fetchWithToken";
import { formatDateRelative } from "../../utils/formatDateRelative";

const PostList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [sortOption, setSortOption] = useState("");
  const [sortedPosts, setSortedPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [, setTimeState] = useState(Date.now()); // État pour forcer le re-rendu basé sur le temps

  // State pour le filtre
  const [filter, setFilter] = useState("");

  // Options dynamiques
  const [dynamicFilterOptions, setDynamicFilterOptions] = useState([
    { value: "", label: "Tous les types" },
  ]);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_BASE_URL}/posts`
        );
        if (!response.ok)
          throw new Error("Erreur lors de la récupération des posts.");
        const data = await response.json();
        setPosts(data);
        setSortedPosts(data);

        // ---------------------
        // Génération dynamique des types
        // ---------------------
        const types = [...new Set(data.map((post) => post.type))]; // types uniques

        const options = [
          { value: "", label: "Tous les types" },
          ...types.map((t) => ({
            value: t,
            label: t.charAt(0).toUpperCase() + t.slice(1),
          })),
        ];

        setDynamicFilterOptions(options);
      } catch (err) {
        setError("Impossible de charger les données : " + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
    const interval = setInterval(() => {
      setTimeState(Date.now()); // Met à jour l'état pour forcer un re-rendu
    }, 59000); // Intervalle de 59 secondes

    return () => clearInterval(interval); // Nettoie l'intervalle lors du démontage
  }, []);

  const handleShowDetails = (post) => {
    setSelectedPost(post);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedPost(null);
  };

  const handleOpenModal = (post) => {
    setSelectedPost(post);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPost(null);
  };

  const handleDelete = async () => {
    if (!selectedPost) return;
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_BASE_URL}/posts/${selectedPost.id}`,
        { method: "DELETE" }
      );
      const result = await response.json();
      if (result.status === "deleted") {
        alert("Post supprimé !");
        setPosts(posts.filter((c) => c.id !== selectedPost.id));
        setSortedPosts(sortedPosts.filter((c) => c.id !== selectedPost.id));
      } else {
        alert("Échec de la suppression.");
      }
    } catch (err) {
      setError("Une erreur est survenue lors de la suppression.");
    } finally {
      handleCloseModal();
    }
  };

  const filteredPosts = sortedPosts
    .filter(
      (post) =>
        // Filtre par type
        filter === "" || post.type === filter
    )
    .filter((post) =>
      // Filtre SearchBar
      post.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <Layout>
      <div className="container mt-2">
        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "80vh" }}
          >
            <Loader />
          </div>
        ) : (
          <>
            <SearchBar
              placeholder="Rechercher un post..."
              onSearch={(query) => setSearchQuery(query)}
              delay={300}
            />
            <HeaderWithFilter
              title="Posts"
              link="/admin-gest/post/add"
              linkText="Ajouter"
              main={posts.length || null}
              sortOption={sortOption}
              setSortOption={setSortOption}
              dataList={posts}
              setSortedList={setSortedPosts}
              dateField="created_at"
              filter={filter}
              setFilter={setFilter}
              filterOptions={dynamicFilterOptions}
            />
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {filteredPosts.length > 0 ? (
                filteredPosts.map((post, index) => (
                  <div
                    key={post.id || index}
                    className="card shadow-sm border"
                    style={{
                      width: "260px",
                      borderRadius: "0.75rem",
                      flex: "0 1 auto",
                    }}
                  >
                    {/* IMAGE */}
                    {post.image ? (
                      <img
                        src={post.image}
                        alt={post.title}
                        className="card-img-top"
                        style={{
                          height: "180px",
                          objectFit: "cover",
                          borderRadius: "0.5rem",
                        }}
                      />
                    ) : (
                      <div
                        className="w-100 d-flex align-items-center justify-content-center bg-light text-secondary"
                        style={{
                          height: "180px",
                          borderRadius: "0.5rem",
                        }}
                      >
                        <i className="fa fa-newspaper fa-3x"></i>
                      </div>
                    )}

                    {/* CONTENU */}
                    <div className="card-body d-flex flex-column justify-content-between">
                      <div>
                        <p className="badge rounded-pill border border-secondary text-secondary text-muted small mb-3 text-capitalize">
                          {post.type}
                        </p>
                        {/* TITRE */}
                        <h6 className="card-title mb-1">{post.title}</h6>

                        {/* DATES */}
                        {post.publish_at && (
                          <p className="small text-muted mb-1 text-end">
                            <i className="fa fa-calendar-alt me-1"></i>
                            Date de pub. :{" "}
                            <strong>
                              {post.publish_at
                                ? formatDateRelative(post.publish_at)
                                : "---"}
                            </strong>
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="small text-muted opacity-75 text-end mt-auto">
                          <i className="fa fa-clock me-1"></i>
                          Créé :{" "}
                          <strong>
                            {post.created_at
                              ? formatDateRelative(post.created_at)
                              : "---"}
                          </strong>
                        </p>

                        {/* ACTIONS */}
                        <div className="d-flex justify-content-between mt-2">
                          <button
                            onClick={() => handleShowDetails(post)}
                            className="btn btn-outline-info btn-sm"
                            title="Voir"
                          >
                            <i className="fas fa-eye"></i>
                          </button>

                          <Link
                            to={`/admin-gest/update/post/${post.id}`}
                            className="btn btn-outline-warning btn-sm"
                            title="Modifier"
                          >
                            <i className="fas fa-edit"></i>
                          </Link>

                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Supprimer"
                            onClick={() => handleOpenModal(post)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted text-center w-100 py-4">
                  Aucun post trouvé.
                </p>
              )}
            </div>
          </>
        )}
      </div>
      <Modal show={showDetails} onHide={handleCloseDetails} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Détails du Post</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPost && (
            <div className="container">
              <div className="row mb-2">
                <div className="col-6 fw-bold">Titre</div>
                <div className="col-6">{selectedPost.title}</div>
              </div>
              <div className="row mb-2">
                <div className="col-6 fw-bold">Post</div>
                <div className="text-justify">{selectedPost.content}</div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDetails}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmPopup
        show={showModal}
        onClose={handleCloseModal}
        onConfirm={handleDelete}
        title="Confirmer la suppression"
        body={
          <p>
            Voulez-vous vraiment supprimer le post{" "}
            <strong>{selectedPost?.title}</strong> ?
          </p>
        }
      />
    </Layout>
  );
};

export default PostList;
